import { OrderFilters, OrdersResponse } from "../types/order";
import { normalizeOrderFinancialCollection } from "../utils/orderFinancials";
import { withServiceFeeAlias } from "../utils/serviceFeePayload";

import { ApiClient } from "./apiClient";

// Item Status types for timeline
export interface ItemStatus {
  id: number;
  name: string;
  displayName: string;
  description: string;
  color: string;
  icon: string;
  sortOrder: number;
  isActive: boolean;
  canTransitionTo: number[];
  requiresTrackingNumber: boolean;
  requiresNote: boolean;
  createdAt: string;
  updatedAt: string;
  orderItemCount: number;
  allowedNextStatusIds: number[];
  isFinalStatus: boolean;
}

export interface ItemStatusesResponse {
  data: ItemStatus[];
  isSuccess: boolean;
  message: string;
  errors: string[];
  statusCode: number;
}

const orderService = new ApiClient<OrdersResponse>("/Order");

export const getOrders = async (
  filters?: OrderFilters
): Promise<OrdersResponse> => {
  const params = new URLSearchParams();

  if (filters?.startDate) params.append("StartDate", filters.startDate);
  if (filters?.endDate) params.append("EndDate", filters.endDate);
  if (filters?.sortBy) params.append("SortBy", filters.sortBy);
  if (filters?.sortDescending !== undefined)
    params.append("SortDescending", filters.sortDescending.toString());
  if (filters?.pageNumber)
    params.append("PageNumber", filters.pageNumber.toString());
  if (filters?.pageSize) params.append("PageSize", filters.pageSize.toString());

  const queryString = params.toString();
  const url = `/user-orders-with-items${queryString ? `?${queryString}` : ""}`;

  const response = await orderService.custom("get", url);
  return {
    ...response,
    data: {
      ...response.data,
      items: normalizeOrderFinancialCollection(response.data.items || []),
    },
  };
};

// Get active item statuses for frontend (non-admin endpoint)
// Uses /Order/active-item-statuses instead of /admin/item-statuses
// No pagination needed - max ~10 records
export const getItemStatuses = async (): Promise<ItemStatusesResponse> => {
  const response = await orderService.custom("get", "/active-item-statuses");
  // Backend returns Result<List<OrderItemStatusDto>>, return as simple list
  return {
    data: response.isSuccess && response.data ? response.data : [],
    isSuccess: response.isSuccess,
    message:
      response.message ||
      (response.isSuccess
        ? "Item statuses retrieved successfully"
        : "Failed to retrieve item statuses"),
    errors: response.errors || [],
    statusCode: response.statusCode || (response.isSuccess ? 200 : 500),
  };
};

export interface CreateOrderRequest {
  userId?: string;
  totalPrice: number;
  shippingCost: number;
  tax: number;
  fees?: number;
  finalTotal: number;
  items: Array<{
    productId: string;
    title: string;
    price: number;
    quantity: number;
    image?: string;
    retailer?: string;
    estimatedWeight?: number;
    weightInKg?: number;
    cargoPrice?: number;
    sellerName?: string;
    sellerId?: string;
    offerId?: string;
    sellerShippingFee?: number;
  }>;
  deliveryAddressId: string;
  deliveryInstructions?: string;
  pickupLocation: string;
  paymentMethodType?: number; // 2 for Alif, 3 for DC Card
  manualPaymentDetails?: {
    transactionId?: string;
    customerPhone: string;
    screenshotUrl?: string;
  };
  // Promotion code information
  promotionCode?: string;
  promotionCodeId?: string;
  promotionDiscountAmount?: number;
  discountAmount?: number;
  // Payment method discount information
  paymentMethodDiscountId?: string;
  paymentMethodDiscountAmount?: number;
}

export interface CreateOrderResponse {
  orderId: string;
  orderNumber: string;
}

export const createOrder = async (
  orderData: CreateOrderRequest
): Promise<{
  isSuccess: boolean;
  data?: CreateOrderResponse;
  message: string;
  errors?: string[];
  statusCode: number;
}> => {
  const response = await orderService.custom(
    "post",
    "/create",
    withServiceFeeAlias(orderData)
  );
  return {
    isSuccess: response.isSuccess,
    data: response.data,
    message: response.message || "",
    errors: response.errors,
    statusCode: response.statusCode || (response.isSuccess ? 200 : 500),
  };
};

// Export orderService instance for use in components
export { orderService };
